/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { ErrorCodes } from "@tivocorp/data-domain-lib";

import { ErrorCodeTypes } from "../enums/ErrorCodeTypes";

/**
 * Utility class for mapping ErrorCodeTypes enum values to their corresponding
 * error message suffix codes.
 *
 * The suffix codes are used to construct complete error messages with
 * context-specific information.
 *
 * Error codes are categorized into:
 * - Network errors (N6xx): Connection, server, and network-related issues
 * - Service context errors (S6xx): Authentication, authorization, and
 *   service-level issues
 * - HTTP errors (G4xx-G6xx): Standard HTTP status code related errors
 */
export class ErrorCodeSuffixMapper {
  /**
   * Gets the suffix error code string for a given ErrorCodeTypes.
   * This method maps each ErrorCodeTypes enum value to its corresponding
   * message ID suffix.
   *
   * @param errorCodeType - The ErrorCodeTypes enum value to map
   * @returns The suffix error string (e.g., 'N601', 'S601', 'G400') or
   *          empty string if no mapping exists
   *
   * @example
   * ```typescript
   * const suffix = ErrorCodeSuffixMapper.getSuffixCodeFromErrorType(
   *     ErrorCodeTypes.UNKNOWN_SERVER
   * );
   * // Returns: 'N601'
   * ```
   */
  public static getSuffixCodeFromErrorType(
    errorCodeType: ErrorCodeTypes
  ): string {
    let suffixErrorString = "";

    // eslint-disable-next-line sonarjs/max-switch-cases
    switch (errorCodeType) {
      // Network errors (N6xx)
      case ErrorCodeTypes.UNKNOWN_SERVER:
        suffixErrorString = ErrorCodes.NETWORK_UNKNOWN_SERVER;
        break;
      case ErrorCodeTypes.CONNECTION_REFUSED:
        suffixErrorString = ErrorCodes.NETWORK_CONNECTION_REFUSED;
        break;
      case ErrorCodeTypes.CONNECTION_FAILED:
        suffixErrorString = ErrorCodes.NETWORK_CONNECTION_FAILED;
        break;
      case ErrorCodeTypes.HTTP_BAD_REQUEST:
        suffixErrorString = ErrorCodes.NETWORK_HTTP_BAD_REQUEST;
        break;
      case ErrorCodeTypes.BAD_RESPONSE:
        suffixErrorString = ErrorCodes.NETWORK_BAD_RESPONSE;
        break;
      case ErrorCodeTypes.SHORT_RESPONSE:
        suffixErrorString = ErrorCodes.NETWORK_SHORT_RESPONSE;
        break;
      case ErrorCodeTypes.IO:
        suffixErrorString = ErrorCodes.NETWORK_IO_ERROR;
        break;
      case ErrorCodeTypes.FILE_NOT_FOUND:
        suffixErrorString = ErrorCodes.NETWORK_FILE_NOT_FOUND;
        break;
      case ErrorCodeTypes.GENERAL:
        suffixErrorString = ErrorCodes.NETWORK_GENERAL_ERROR;
        break;

      // Service context errors (S6xx)
      case ErrorCodeTypes.BRIDGEKEEPER_CONNECTION_DENIED:
        suffixErrorString = ErrorCodes.SERVICE_BRIDGEKEEPER_DENIED;
        break;
      case ErrorCodeTypes.SERVICE_CONTEXT_CONNECTION_FAILED:
        suffixErrorString = ErrorCodes.SERVICE_CONNECTION_FAILED;
        break;
      case ErrorCodeTypes.CONNECTION_LOST:
        suffixErrorString = ErrorCodes.SERVICE_CONNECTION_LOST;
        break;
      case ErrorCodeTypes.INVALID_RESPONSE:
        suffixErrorString = ErrorCodes.SERVICE_INVALID_RESPONSE;
        break;
      case ErrorCodeTypes.SERVICE_CONTEXT_INTERNAL_ERROR:
        suffixErrorString = ErrorCodes.SERVICE_INTERNAL_ERROR;
        break;
      case ErrorCodeTypes.CREDENTIAL_FAILED:
        suffixErrorString = ErrorCodes.SERVICE_CREDENTIAL_FAILED;
        break;
      case ErrorCodeTypes.CREDENTIAL_EXPIRED:
        suffixErrorString = ErrorCodes.SERVICE_CREDENTIAL_EXPIRED;
        break;
      case ErrorCodeTypes.CREDENTIAL_NOT_AUTHORIZED:
        suffixErrorString = ErrorCodes.SERVICE_CREDENTIAL_NOT_AUTHORIZED;
        break;
      case ErrorCodeTypes.FAILURE_RETRIEVE_DEVICE_LIST:
        suffixErrorString = ErrorCodes.SERVICE_DEVICE_LIST_RETRIEVAL_FAILED;
        break;
      case ErrorCodeTypes.NO_AUTHENTICATE_RESPONSE:
        suffixErrorString = ErrorCodes.SERVICE_NO_AUTHENTICATE_RESPONSE;
        break;
      case ErrorCodeTypes.SERVICE_CONTEXT_MIDDLEMIND_ERROR:
        suffixErrorString = ErrorCodes.SERVICE_MIDDLEMIND_ERROR;
        break;
      case ErrorCodeTypes.TIMEOUT:
        suffixErrorString = ErrorCodes.SERVICE_TIMEOUT;
        break;
      case ErrorCodeTypes.SERVER_NOT_RESOLVED:
        suffixErrorString = ErrorCodes.SERVICE_SERVER_NOT_RESOLVED;
        break;
      case ErrorCodeTypes.INVALID_CONTEXT_STATE:
        suffixErrorString = ErrorCodes.SERVICE_INVALID_CONTEXT_STATE;
        break;
      case ErrorCodeTypes.CONTEXT_DESTROYED:
        suffixErrorString = ErrorCodes.SERVICE_CONTEXT_DESTROYED;
        break;
      case ErrorCodeTypes.SCHEMA_EXCEPTION:
        suffixErrorString = ErrorCodes.SERVICE_SCHEMA_EXCEPTION;
        break;
      case ErrorCodeTypes.INVALID_ACCESS_TOKEN:
        suffixErrorString = ErrorCodes.SERVICE_INVALID_ACCESS_TOKEN;
        break;
      case ErrorCodeTypes.REQUEST_CONTEXT_ERROR:
        suffixErrorString = ErrorCodes.SERVICE_REQUEST_CONTEXT_ERROR;
        break;

      // HTTP errors (G4xx - G6xx)
      case ErrorCodeTypes.BAD_REQUEST:
        suffixErrorString = ErrorCodes.HTTP_BAD_REQUEST;
        break;
      case ErrorCodeTypes.NOT_AUTHORIZED:
        suffixErrorString = ErrorCodes.HTTP_UNAUTHORIZED;
        break;
      case ErrorCodeTypes.FORBIDDEN:
        suffixErrorString = ErrorCodes.HTTP_FORBIDDEN;
        break;
      case ErrorCodeTypes.OBJECT_NOT_FOUND:
        suffixErrorString = ErrorCodes.HTTP_NOT_FOUND;
        break;
      case ErrorCodeTypes.NOT_ALLOWED:
        suffixErrorString = ErrorCodes.HTTP_METHOD_NOT_ALLOWED;
        break;
      case ErrorCodeTypes.CONFLICT:
        suffixErrorString = ErrorCodes.HTTP_CONFLICT;
        break;
      case ErrorCodeTypes.UNSUPPORTED_MEDIA_TYPE:
        suffixErrorString = ErrorCodes.HTTP_UNSUPPORTED_MEDIA_TYPE;
        break;
      case ErrorCodeTypes.TOO_MANY_REQUESTS:
        suffixErrorString = ErrorCodes.HTTP_TOO_MANY_REQUESTS;
        break;
      case ErrorCodeTypes.INTERNAL_ERROR:
        suffixErrorString = ErrorCodes.HTTP_INTERNAL_SERVER_ERROR;
        break;
      case ErrorCodeTypes.BAD_GATEWAY:
        suffixErrorString = ErrorCodes.HTTP_BAD_GATEWAY;
        break;
      case ErrorCodeTypes.SERVICE_UNAVAILABLE:
        suffixErrorString = ErrorCodes.HTTP_SERVICE_UNAVAILABLE;
        break;
      case ErrorCodeTypes.SERVER_TIMEOUT:
        suffixErrorString = ErrorCodes.HTTP_GATEWAY_TIMEOUT;
        break;
      case ErrorCodeTypes.UNHANDLED_ERROR:
        suffixErrorString = ErrorCodes.HTTP_GENERIC_ERROR;
        break;
    }

    return suffixErrorString;
  }
}
